/*
 * Members shared by TPRM third parties and UCRM businesses, which are both organizations. `Models/TPRM.ts` and `Models/Ucrm.ts` define the module-specific interfaces that extend these.
 */
import { ExceptionResponse } from './Exceptions';
import { IssueResponse } from './Issues';
import { DisplayableTag } from './Tags';
import { OptionType } from './Types/GlobalType';

export enum ActiveState {
    ACTIVE = 'Active',
    INACTIVE = 'Inactive',
}

export enum ActiveStateDisplayOption {
    ACTIVE_ONLY = 'Show active only',
    INACTIVE_ONLY = 'Show inactive only',
    ALL = 'Show all',
}

export const activeStateFilterOptions: OptionType[] = [
    { value: ActiveStateDisplayOption.ACTIVE_ONLY, label: ActiveStateDisplayOption.ACTIVE_ONLY },
    { value: ActiveStateDisplayOption.INACTIVE_ONLY, label: ActiveStateDisplayOption.INACTIVE_ONLY },
    { value: ActiveStateDisplayOption.ALL, label: ActiveStateDisplayOption.ALL },
];

export enum FinalizationState {
    DRAFT_IN_PROGRESS = 'Draft (in progress)',
    DRAFT_SUBMITTED_TO_BE_FINALIZED = 'Draft (submitted to be finalized)',
    FINALIZED = 'Finalized',
}

/** The members shared by a TPRM third party (`ThirdParty`) and a UCRM business (`Business`). */
export interface OrganizationBase {
    active_state: ActiveState;
    created_time: string;
    finalization_state: FinalizationState;
    id: string;
    name: string;
    tags: string[]; // Optional from a user perspective, but not from a code perspective since this will always at a minimum be an empty list.
    vendor_manager_user_id: string;

    finalized_by?: string; // Optional because of legacy data. All new organizations have a value for this field once finalized.
    finalized_time?: string; // Optional because of legacy data. All new organizations have a value for this field once finalized.
}

/** The shape of an organization within a historical snapshot of a due diligence workflow, in which tags are stored for display (since the tags may have since been changed or deleted). Satisfied by both `ThirdPartyArchive` (TPRM) and `BusinessArchive` (UCRM). */
export type OrganizationArchiveBase = Omit<OrganizationBase, 'tags'> & { tags: DisplayableTag[] };

/** The members shared by `CreateThirdPartyRequest` (TPRM) and `CreateBusinessRequest` (UCRM). */
export interface CreateOrganizationRequestBase {
    finalization_state: FinalizationState;
    name: string;
    tags: string[];
    vendor_manager_user_id: string;
}

/** The members shared by `UpdateThirdPartyRequest` (TPRM) and `UpdateBusinessRequest` (UCRM). Only the properties that have changed are included. */
export interface UpdateOrganizationRequestBase {
    name?: string;
    tags?: string[];
    vendor_manager_user_id?: string;
}

/** The members shared by `ThirdPartyResponse` (TPRM) and `BusinessResponse` (UCRM). Deliberately not derived from `OrganizationBase`, since responses do not include every member of the domain object. */
export interface OrganizationResponseBase {
    active_state: ActiveState;
    created_time: string;
    exceptions: ExceptionResponse[];
    finalization_state: FinalizationState;
    id: string;
    issues: IssueResponse[];
    name: string;
    tags: string[];
    vendor_manager_user_id: string;
}

/**
 * The members shared by `ThirdPartyForNonTprmUser` (TPRM) and `BusinessForNonUcrmUser` (UCRM).
 * Defined separately from `OrganizationBase` so as to avoid someone inadvertently adding sensitive information to the domain interfaces that will not actually be returned to the frontend for non-privileged users.
 */
export interface OrganizationForNonPrivilegedUserBase {
    active_state: ActiveState;
    created_time: string;
    finalization_state: FinalizationState;
    id: string;
    name: string;
    tags: string[];
    vendor_manager_user_id: string;
}

export const userHasFullAccessOrCreatedDraft = (userId: string, userHasFullAccess: boolean, organizationOrAssessmentSubject: { finalization_state: FinalizationState; created_by?: string }): boolean => {
    if (userHasFullAccess) {
        return true;
    } else {
        return organizationOrAssessmentSubject.finalization_state !== FinalizationState.FINALIZED && organizationOrAssessmentSubject.created_by === userId;
    }
};

/**
 * Whether the user can submit (if unprivileged) or finalize (if they have full access) the draft organization or assessment subject via the intake flow.
 * Unprivileged users can only submit drafts they created that are still in progress; once submitted, a draft can be edited by its creator but not submitted again.
 */
export const userCanSubmitOrFinalizeDraft = (userId: string, userHasFullAccess: boolean, organizationOrAssessmentSubject: { finalization_state: FinalizationState; created_by?: string }): boolean => {
    if (userHasFullAccess) {
        return organizationOrAssessmentSubject.finalization_state !== FinalizationState.FINALIZED;
    } else {
        return userHasFullAccessOrCreatedDraft(userId, userHasFullAccess, organizationOrAssessmentSubject) && organizationOrAssessmentSubject.finalization_state === FinalizationState.DRAFT_IN_PROGRESS;
    }
};
